import {useMemo} from 'react';

import {ProjectSexes} from '@shared/api/app_api';
import {DB_JSON_URL} from '@shared/env';

import {compress, decompress} from '@shared-web/lib/compression';
import {createDataStore} from '@shared-web/lib/data_store';

const DB_KEY = 'db-2023';

type Sex = 'boy' | 'girl';
type RawDb = {
  phonetic: string;
  sex: Sex;
  total: number;
  totalByYear: Record<string, number>;
  totalByName: Record<string, number>;
}[];

export type ParsedDb = {
  key: string; // <phonetic>_<sex>
  sex: Sex;
  phonetic: string;
  rankRecent: number;
  total: number;
  totalRecent: number;
  totalByYear: Record<string, number>;
  totalByName: {name: string; count: number}[];
}[];

export function loadDb(
  rawDb: RawDb,
  opts: {recentYear: number; minRecent: number; sexes: Sex[]}
): ParsedDb {
  const {recentYear, minRecent, sexes} = opts;
  const parsed: ParsedDb = [];
  for (const [_, nameData] of Object.entries(rawDb)) {
    let totalRecent = 0;
    const {phonetic, total, sex, totalByYear, totalByName} = nameData;
    if (!sexes.includes(sex)) {
      continue;
    }
    let recent = 0;
    for (const [year, count] of Object.entries(totalByYear)) {
      if (parseFloat(year) >= recentYear) {
        recent += count;
      }
    }
    totalRecent += recent;
    if (totalRecent >= minRecent) {
      parsed.push({
        key: `${phonetic}_${sex}`,
        sex,
        phonetic,
        total,
        totalRecent,
        rankRecent: -1,
        totalByName: Object.entries(totalByName)
          .sort((e1, e2) => e2[1] - e1[1])
          .map(([name, count]) => ({name, count})),
        totalByYear,
      });
    }
  }
  return parsed
    .sort((e1, e2) => e2.totalRecent - e1.totalRecent)
    .map((entry, i) => ({...entry, rankRecent: i + 1}));
}

let rawDb: RawDb | undefined;
export async function downloadDb(): Promise<RawDb> {
  if (!rawDb) {
    const localDb = localStorage.getItem(DB_KEY);
    if (localDb !== null) {
      const decompressed = await decompress(localDb);
      // eslint-disable-next-line require-atomic-updates
      rawDb = JSON.parse(decompressed) as RawDb;
    } else {
      const res = await fetch(DB_JSON_URL);
      const dbStr = await res.text();
      const db = JSON.parse(dbStr) as RawDb;
      const compressed = await compress(dbStr);
      localStorage.setItem(DB_KEY, compressed);
      // eslint-disable-next-line require-atomic-updates
      rawDb = db;
    }
  }
  return rawDb;
}

const dbBoysDataStore = createDataStore<ParsedDb>();
export const getBoysDb = dbBoysDataStore.getData;
export const setBoysDb = dbBoysDataStore.setData;
export const useBoysDb = dbBoysDataStore.useData;

const dbGirlsDataStore = createDataStore<ParsedDb>();
export const getGirlsDb = dbGirlsDataStore.getData;
export const setGirlsDb = dbGirlsDataStore.setData;
export const useGirlsDb = dbGirlsDataStore.useData;

const dbBoysAndGirlsDataStore = createDataStore<ParsedDb>();
export const getBoysAndGirlsDb = dbBoysAndGirlsDataStore.getData;
export const setBoysAndGirlsDb = dbBoysAndGirlsDataStore.setData;
export const useBoysAndGirlsDb = dbBoysAndGirlsDataStore.useData;

export function useDb(sexes: ProjectSexes): ParsedDb | undefined {
  const boysAndGirlsDb = useBoysAndGirlsDb();
  const boysDb = useBoysDb();
  const girlsDb = useGirlsDb();

  const db = useMemo((): ParsedDb | undefined => {
    if (sexes.length === 2) {
      return boysAndGirlsDb;
    } else if (sexes[0] === 'boy') {
      return boysDb;
    } else if (sexes[0] === 'girl') {
      return girlsDb;
    }
    return undefined;
  }, [boysAndGirlsDb, boysDb, girlsDb, sexes]);

  return db;
}
